import React from 'react';
import { useRef, useEffect } from 'react';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const getContactMethodIcon = (method) => {
  switch (method) {
    case 'Email':
      return <EmailIcon fontSize="inherit" />;
    case 'SMS':
      return <SmsIcon fontSize="inherit" />;
    case 'Phone':
      return <PhoneIcon fontSize="inherit" />;
    default:
      return <ChatBubbleOutlineIcon fontSize="inherit" />;
  }
};

const TimelineRow = ({ accent, icon, title, timestamp, body, from, isLast }) => (
  <div className={`relative pl-9 ${isLast ? '' : 'pb-5'}`}>
    {!isLast && (
      <div className="absolute left-[13px] top-6 bottom-0 w-px bg-[var(--ts-border)]" />
    )}
    <div
      className="absolute left-0 top-0 flex h-[26px] w-[26px] items-center justify-center rounded-full"
      style={{ backgroundColor: `${accent}1f`, color: accent }}
    >
      {icon}
    </div>
    <div className="flex items-baseline justify-between gap-2">
      <Typography variant="body2" className="!font-semibold">
        {title}
      </Typography>
      <Typography variant="caption" className="whitespace-nowrap text-[var(--Grey-70)]">
        {timestamp}
      </Typography>
    </div>
    {body && (
      <Typography variant="body2" className="mt-0.5 text-[var(--ts-text-dark)]">
        {body}
      </Typography>
    )}
    {from && (
      <Typography variant="caption" className="text-[var(--Grey-70)]">
        {from}
      </Typography>
    )}
  </div>
);

const ActivityContacts = ({ contacts, colourScheme = 'display', classList = '' }) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const bottomRef = useRef(null);

  useEffect(() => {
    // Scrolls to the dummy div every time contacts update
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [contacts]);

  return (
    <div
      className={`activity-contacts flex w-full flex-col rounded-2xl border border-[var(--ts-border)] bg-[var(--ts-surface)] p-4 ${classList}`}
    >
      <Typography variant="h4" className="!font-bold mb-3">
        Contacts
      </Typography>
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-[var(--ts-primary)] scrollbar-track-transparent">
        {contacts.length === 0 && (
          <Typography variant="body1" className="text-center text-[var(--Grey-70)]">
            No contacts yet.
          </Typography>
        )}
        {contacts.length > 0 &&
          [...contacts]
            .sort((a, b) => new Date(a.ContactDate) - new Date(b.ContactDate))
            .map((contact, index, arr) => {
              const isInternal =
                contact.ContactTrigger === 'Automation' ||
                contact.ContactTrigger === 'Internal User';
              const accent = isInternal ? 'var(--ts-primary)' : 'var(--ts-accent)';
              return (
                <TimelineRow
                  key={index}
                  accent={accent}
                  icon={getContactMethodIcon(contact.ContactMethod)}
                  title={
                    contact.ContactType === 'User response'
                      ? 'Response'
                      : contact.ContactType
                  }
                  timestamp={dayjs.utc(contact.ContactDate).local().format('D MMM h:mm A')}
                  body={contact.ContactText}
                  from={contact.FromFullName}
                  isLast={index === arr.length - 1}
                />
              );
            })}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

export default ActivityContacts;
